import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
    { label: "Services", href: "#services" },
    { label: "Process", href: "#process" },
    { label: "Calculator", href: "#calculator" },
    { label: "Stories", href: "#stories" },
    { label: "Contact", href: "#contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 30);
            // Reveal on scroll up, conceal on scroll down (past the hero)
            if (y > 140 && y > lastY + 4) setHidden(true);
            else if (y < lastY - 4) setHidden(false);
            lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="main-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                hidden && !open ? "-translate-y-full" : "translate-y-0"
            } ${
                scrolled
                    ? "bg-ink/[0.97] backdrop-blur-xl border-b border-black/40 shadow-lg shadow-black/20 text-cream"
                    : "bg-transparent text-cream"
            }`}
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <a href="#top" data-testid="logo-link" className="flex items-center gap-3 group">
                    <span className="w-7 h-7 border border-gold/70 flex items-center justify-center rotate-45 group-hover:rotate-[225deg] transition-transform duration-700">
                        <span className="w-1.5 h-1.5 bg-gold" />
                    </span>
                    <span className="font-serif text-2xl tracking-tight">
                        Quick<span className="text-gold">Fin</span>
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                            className="font-mono text-xs uppercase tracking-[0.2em] opacity-80 hover:opacity-100 hover:text-gold gold-underline transition-colors"
                        >
                            {n.label}
                        </a>
                    ))}
                </nav>

                <a
                    href="#contact"
                    data-testid="nav-cta-button"
                    className="hidden md:inline-flex items-center gap-2 bg-gold text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-forest transition-colors"
                >
                    Request Access
                    <span>→</span>
                </a>

                <button
                    data-testid="mobile-menu-toggle"
                    className="md:hidden"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-ink/95 backdrop-blur-xl border-t border-ink/50 text-cream">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                                className="font-mono text-xs uppercase tracking-[0.2em] opacity-80 hover:opacity-100 hover:text-gold"
                            >
                                {n.label}
                            </a>
                        ))}
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-cta-button"
                            className="inline-flex items-center justify-center bg-ink text-cream px-5 py-3 font-mono text-xs uppercase tracking-[0.2em]"
                        >
                            Request Access →
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
